import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import './App.css';
import { CompaniesPage, CompanyPage } from './Companies';
import { ClientPage, ClientsPage } from './Clients';
import { ClientDetailPage, ClientDetailsPage } from './ClientDetails';
import { SupplierPage, SuppliersPage } from './Suppliers';
import { ProductPage, ProductsPage } from './Products';
import { QuotePage, QuotesPage } from './Quotes';
import { Font } from '@react-pdf/renderer';
import { StatusFlowPage } from './StatusFlow';
import { QuoteDeliveryPage } from './QuotesDelivery';
import { createTheme, CssBaseline, GlobalStyles, ThemeProvider, useMediaQuery } from '@mui/material';
import { hexFromColorName, User, UserContext, UserPage, UserPalette, UsersPage } from './Users';
import { SignInPage, SignOutPage } from './Login';
import { RequestPage, RequestsPage } from './Requests';
import { ExplorePage } from './Explore';

function App() {
    Font.register({
        family: 'Roboto',
        fonts: [
            { src: '/fonts/Roboto-Regular.ttf' },
            { src: '/fonts/Roboto-Bold.ttf', fontWeight: 'bold' },
            { src: '/fonts/Roboto-Italic.ttf', fontStyle: 'italic' },
            { src: '/fonts/Roboto-BoldItalic.ttf', fontWeight: 'bold', fontStyle: 'italic' },
        ],
    });
    Font.register({
        family: 'Arial',
        fonts: [
            { src: '/fonts/Arial-Regular.ttf' },
            { src: '/fonts/Arial-Bold.ttf', fontWeight: 'bold' },
            { src: '/fonts/Arial-Italic.ttf', fontStyle: 'italic' },
            { src: '/fonts/Arial-BoldItalic.ttf', fontWeight: 'bold', fontStyle: 'italic' },
        ],
    });

    const [user, setUser] = useState<Partial<User>>({});
    useEffect(() => {
        if (user.username === undefined && (user as any).tried === undefined) {
            const fetchUser = async () => {
                const response = await fetch("/api/v1/user-auth/token/sign-in", { method: "POST" });
                const data = await response.json();
                if (data !== undefined) {
                    if (data.result === undefined) {
                        data.result = {tried: true};
                    } else {
                        data.result.tried = true;
                    }
                }
                setUser(data.result || {});
            }
            fetchUser();
        }
    }, [user]);
    const [palette, setPalette] = useState<UserPalette>({ 
        main: localStorage.getItem("theme.palette.main") || "blue", 
        secondary: localStorage.getItem("theme.palette.secondary") || "blue",
        mode: (localStorage.getItem("theme.mode") as any) || "system",
    });
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    const darkTheme = createTheme({
        colorSchemes: {
            dark: palette.mode === "dark",
        },
        palette: {
            mode: palette.mode === "system" 
                ? (prefersDarkMode ? "dark" : "light")
                : palette.mode,
            primary: { main: hexFromColorName(palette.main) },
            secondary: { main: hexFromColorName(palette.secondary) },
        },
        components: {
            MuiInputBase: {
                defaultProps: {
                    disableInjectingGlobalStyles: true,
                },
            },
        },
    });

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <GlobalStyles styles={{ 
                h1: { textAlign: "center" }, 
                a: { cursor: "pointer" },
                '@keyframes mui-auto-fill': { from: { display: 'block' } },
                '@keyframes mui-auto-fill-cancel': { from: { display: 'block' } },
            }}/>
            <UserContext value={{user: user, setUser: setUser, palette: palette, setPalette: setPalette }} >
                <Router>
                    <Routes>
                        <Route path="/" element={<SignInPage />} />
                        <Route path="/home" element={<StatusFlowPage />} />
                        <Route path="/home/:tab" element={<StatusFlowPage />} />
                        <Route path="/companies" element={<CompaniesPage />} />
                        <Route path="/companies/:companyIdText" element={<CompanyPage />} />
                        <Route path="/companies/:companyIdText/clients" element={<ClientsPage />} />
                        <Route path="/companies/:companyIdText/quotes" element={<QuotesPage />} />
                        <Route path="/companies/:companyIdText/quotes/page/:pageIdText" element={<QuotesPage />} />
                        <Route path="/companies/:companyIdText/quotes/:quoteNoText" element={<QuotePage />} />

                        <Route path="/clients" element={<ClientsPage />} />
                        <Route path="/clients/:clientIdText" element={<ClientPage />} />
                        <Route path="/clients/:clientIdText/details" element={<ClientDetailsPage />} />
                        <Route path="/clients/:clientIdText/quotes" element={<QuotesPage />} />
                        <Route path="/clients/:clientIdText/quotes/page/:pageIdText" element={<QuotesPage />} />
                        <Route path="/clients/:clientIdText/quotes/:quoteNoText" element={<QuotePage />} />

                        <Route path="/client-details/:clientDetailIdText" element={<ClientDetailPage />} />
                        <Route path="/client-details/:clientDetailIdText/quotes" element={<QuotesPage />} />
                        <Route path="/client-details/:clientDetailIdText/quotes/page/:pageIdText" element={<QuotesPage />} />
                        <Route path="/client-details/:clientDetailIdText/quotes/:quoteNoText" element={<QuotePage />} />

                        <Route path="/suppliers" element={<SuppliersPage />} />
                        <Route path="/suppliers/:supplierIdText" element={<SupplierPage />} />
                        <Route path="/suppliers/:supplierIdText/products" element={<ProductsPage />} />
                        <Route path="/suppliers/:supplierIdText/products/page/:pageIdText" element={<ProductsPage />} />
                        <Route path="/suppliers/:supplierIdText/products/:productIdText" element={<ProductPage />} />

                        <Route path="/quotes" element={<QuotesPage />} />
                        <Route path="/quotes/page/:pageIdText" element={<QuotesPage />} />
                        <Route path="/quotes/:quoteNoText" element={<QuotePage />} />
                        <Route path="/quotes/delivery/:quoteNoText" element={<QuoteDeliveryPage />} />

                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/users/:username" element={<UserPage />} />
                        <Route path="/users/sign-out/:signOutUsername" element={<SignOutPage />} />

                        <Route path="/requests" element={<RequestsPage />} />
                        <Route path="/requests/:requestIdText" element={<RequestPage />} />

                        <Route path="/explore" element={<ExplorePage />} />
                    </Routes>
                </Router>
            </UserContext>
        </ThemeProvider>
    );
}

export default App;
